"use client";

import { useMutation } from "@apollo/client";
import AIHelperCV from "components/AIHelper/ai-helper-cv";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { gqlUser } from "gql";
import { ArrowDown, ArrowUp, Bot, ExternalLink, Plus, RotateCcw, Save, Sparkles, Trash2 } from "lucide-react";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { getQueryOperator } from "utils/helpers";
import {
  Button,
  Card,
  DatePickerV2,
  Input,
  LiquidPill,
  LiquidPillExpandButton,
  Modal,
  Textarea,
} from "components/ui/v2";
import { ResumeTabHeaderV2 } from "./ResumeTabHeaderV2";

function MoveButtons({ index, total, onMoveUp, onMoveDown, onRemove }: { index: number; total: number; onMoveUp: () => void; onMoveDown: () => void; onRemove: () => void }) {
  const { t } = useTranslation("common");
  return (
    <div className="flex items-center gap-2">
      <LiquidPill as="button" type="button" variant="slate" appearance="tinted" shape="circle" size="sm" disabled={index === 0} icon={<ArrowUp className="h-4 w-4" />} onClick={onMoveUp} aria-label={t("Subir")} />
      <LiquidPill as="button" type="button" variant="slate" appearance="tinted" shape="circle" size="sm" disabled={index === total - 1} icon={<ArrowDown className="h-4 w-4" />} onClick={onMoveDown} aria-label={t("Bajar")} />
      <LiquidPill as="button" type="button" variant="rose" appearance="tinted" shape="circle" size="sm" icon={<Trash2 className="h-4 w-4" />} onClick={onRemove} aria-label={t("Eliminar")} />
    </div>
  );
}

export function ResumeExperienceTabV2() {
  const { t } = useTranslation("profile");
  const { user: userData, updateUserData } = useUserStoreContext((store) => ({
    user: store.user,
    updateUserData: store.updateUserData,
  }));
  const [aiHelperOpen, setAiHelperOpen] = useState(false);

  const [updateProfile, { loading: updateProfileLoading }] = useMutation(
    gqlUser.mutations.UPDATE_USER_PROFILE,
    {
      notifyOnNetworkStatusChange: true,
      refetchQueries: [
        getQueryOperator(gqlUser.queries.GET_FULL_USER),
        getQueryOperator(gqlUser.queries.USER_LEVEL),
      ],
      onCompleted: (data) => updateUserData(data.updateProfile),
    }
  );

  const { control, handleSubmit, setValue } = useForm<any>({
    defaultValues: userData ?? { ...(userData || {}) },
  });

  const experiencesFA = useFieldArray({ name: "aiCVInfo._experiences", control });
  const educationFA = useFieldArray({ name: "aiCVInfo._education", control });
  const languagesFA = useFieldArray({ name: "aiCVInfo.languages", control });

  useEffect(() => {
    if (!userData?.aiCVInfo) return;
    const cv = userData.aiCVInfo;
    setValue("aiCVInfo", cv);
    experiencesFA.replace([
      ...(cv.workExperience ? [cv.workExperience] : []),
      ...((cv.extraWorkExperience as any[]) ?? []),
    ]);
    educationFA.replace([
      ...(cv.education ? [cv.education] : []),
      ...(((cv as any).extraEducation as any[]) ?? []),
    ]);
    languagesFA.replace((cv.languages as any[]) ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const handleSaveCV = (data: any) => {
    const { _experiences, _education, ...rest } = data.aiCVInfo;
    const payload = {
      ...rest,
      workExperience: _experiences?.[0] ?? null,
      extraWorkExperience: _experiences?.slice(1) ?? [],
      education: _education?.[0] ?? null,
      extraEducation: _education?.slice(1) ?? [],
    };
    toast.promise(updateProfile({ variables: { updateProfileInput: { aiCVInfo: payload } } }), {
      loading: t("Data.Guardando cambios"),
      success: t("Data.Cambios guardados"),
      error: t("Data.No se pudo guardar los cambios"),
    });
  };

  const handleOpenCV = () => window.open(`/jobyfine/${userData?.id || ""}&collaborator=true`, "_blank");

  return (
    <div className="grid gap-4">
      <ResumeTabHeaderV2
        title={t("Resume.Experiencia Laboral")}
        action={
          <LiquidPillExpandButton
            icon={<ExternalLink className="h-4 w-4" />}
            label={t("Resume.ReviewOnline")}
            variant="sky"
            size="sm"
            expandTo="left"
            onClick={handleOpenCV}
            aria-label={t("Resume.ReviewOnline")}
          />
        }
      />

      <section className="py-2">
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            <div className="absolute inset-0 rounded-full bg-indigo-400/20 blur-xl" />
            <div className="relative flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 shadow-[0_10px_24px_-14px_rgba(79,70,229,0.8)]">
              <Bot className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold tracking-[-0.02em] text-slate-950 dark:text-white">{t("Resume.Title1")}</h3>
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-indigo-500 shadow-[0_2px_10px_-5px_rgba(79,70,229,0.75)] ring-1 ring-indigo-100 dark:bg-zinc-950 dark:ring-indigo-400/20">
                <Sparkles className="h-3.5 w-3.5" />
              </span>
            </div>
            <p className="mt-1.5 max-w-4xl text-sm leading-6 text-slate-600 dark:text-zinc-400">
              {userData?.aiCVInfo?.ofuscatedName && t("Resume.Question1")}
              {userData?.cvUrl && !userData?.aiCVInfo?.ofuscatedName && t("Resume.Instruction0")}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {userData?.aiCVInfo?.ofuscatedName ? (
                <LiquidPillExpandButton icon={<RotateCcw className="h-4 w-4" />} label={t("Resume.Regenerate")} variant="indigo" size="sm" onClick={() => setAiHelperOpen(true)} aria-label={t("Resume.Regenerate")} />
              ) : (
                <>
                  <span className="text-sm text-slate-500 dark:text-zinc-400">{t("Resume.Instruction1")}</span>
                  <LiquidPillExpandButton icon={<Sparkles className="h-4 w-4" />} label={t("Resume.Here")} variant="violet" size="sm" onClick={() => setAiHelperOpen(true)} aria-label={t("Resume.Here")} />
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Card variant="default" padding="none" shadow="subtle" size="full">
        <div className="divide-y divide-slate-200/60 p-3 dark:divide-zinc-800/70">
          <section className="pb-5">
            <Controller
              name="aiCVInfo.tagLine"
              control={control}
              defaultValue={userData?.aiCVInfo?.tagLine}
              render={({ field }) => (
                <Textarea {...field} label={t("Resume.WriteBriefSummary")} placeholder={t("Resume.WriteBriefSummaryPlaceholder")} size="sm" variant="slate" rows={6} />
              )}
            />
          </section>

          <section className="py-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold leading-tight text-slate-950 dark:text-white">{t("Resume.Experiencia Laboral")}</h3>
              <LiquidPillExpandButton icon={<Plus className="h-4 w-4" />} label={t("Resume.Agregar experiencia")} variant="indigo" size="sm" onClick={() => experiencesFA.append({ title: "", description: "", date: "", company: "" })} aria-label={t("Resume.Agregar experiencia")} />
            </div>

            <div className="grid gap-3">
              {experiencesFA.fields.map((experience, i) => (
                <div key={experience.id} className="rounded-2xl border border-slate-200/60 bg-slate-50/80 p-3 dark:border-zinc-800/70 dark:bg-zinc-900/40">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-zinc-400">
                      {t("Resume.Experiencia Laboral")} {i + 1}
                    </p>
                    <MoveButtons index={i} total={experiencesFA.fields.length} onMoveUp={() => experiencesFA.move(i, i - 1)} onMoveDown={() => experiencesFA.move(i, i + 1)} onRemove={() => experiencesFA.remove(i)} />
                  </div>
                  <div className="grid gap-4">
                    <Controller name={`aiCVInfo._experiences.${i}.title`} control={control} render={({ field }) => <Input {...field} label={t("Resume.Cargo")} placeholder={t("Resume.Ejemplo Vendedor de Ideas")} />} />
                    <Controller name={`aiCVInfo._experiences.${i}.description`} control={control} render={({ field }) => <Textarea {...field} label={t("Resume.Descripción de la Experiencia")} placeholder={t("Resume.Descripción del trabajo realizado, puedes agregar logros y responsabilidades")} rows={5} />} />
                    <Controller name={`aiCVInfo._experiences.${i}.date`} control={control} render={({ field }) => <DatePickerV2 label={t("Resume.Fecha")} value={field.value ? moment(field.value, "YYYY-MM-DD").toDate() : null} onChange={(d) => field.onChange(d ? moment(d).format("YYYY-MM-DD") : "")} placeholder={t("Resume.Fecha")} />} />
                    <Controller name={`aiCVInfo._experiences.${i}.company`} control={control} render={({ field }) => <Input {...field} label={t("Resume.Empresa")} placeholder={t("Resume.Ejemplo CleverIT")} />} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold leading-tight text-slate-950 dark:text-white">{t("Resume.Experiencia académica")}</h3>
              <LiquidPillExpandButton icon={<Plus className="h-4 w-4" />} label={t("Resume.Agregar estudio")} variant="indigo" size="sm" onClick={() => educationFA.append({ title: "", description: "", dateStart: "", dateEnd: "" })} aria-label={t("Resume.Agregar estudio")} />
            </div>

            <div className="grid gap-3">
              {educationFA.fields.map((education, i) => (
                <div key={education.id} className="rounded-2xl border border-slate-200/60 bg-slate-50/80 p-3 dark:border-zinc-800/70 dark:bg-zinc-900/40">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-zinc-400">
                      {t("Resume.Experiencia académica")} {i + 1}
                    </p>
                    <MoveButtons index={i} total={educationFA.fields.length} onMoveUp={() => educationFA.move(i, i - 1)} onMoveDown={() => educationFA.move(i, i + 1)} onRemove={() => educationFA.remove(i)} />
                  </div>
                  <div className="grid gap-4">
                    <Controller name={`aiCVInfo._education.${i}.title`} control={control} render={({ field }) => <Input {...field} label={t("Resume.Nombre del Título")} placeholder={t("Resume.Nombre del Título")} />} />
                    <Controller name={`aiCVInfo._education.${i}.description`} control={control} render={({ field }) => <Textarea {...field} label={t("Resume.Descripción del Título")} placeholder={t("Resume.Describe tu título académico")} rows={5} />} />
                    <Controller name={`aiCVInfo._education.${i}.dateStart`} control={control} render={({ field }) => <DatePickerV2 label={t("Resume.Fecha de Inicio de Estudios")} value={field.value ? moment(field.value, "YYYY-MM-DD").toDate() : null} onChange={(d) => field.onChange(d ? moment(d).format("YYYY-MM-DD") : "")} placeholder={t("Resume.Fecha de Inicio de Estudios")} />} />
                    <Controller name={`aiCVInfo._education.${i}.dateEnd`} control={control} render={({ field }) => <DatePickerV2 label={t("Resume.Fecha de Término de Estudios")} value={field.value ? moment(field.value, "YYYY-MM-DD").toDate() : null} onChange={(d) => field.onChange(d ? moment(d).format("YYYY-MM-DD") : "")} placeholder={t("Resume.Fecha de Término de Estudios")} />} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold leading-tight text-slate-950 dark:text-white">{t("Resume.Idioma")}</h3>
              <LiquidPillExpandButton icon={<Plus className="h-4 w-4" />} label={t("Resume.Agregar idioma")} variant="indigo" size="sm" onClick={() => languagesFA.append({ name: "", level: "" })} aria-label={t("Resume.Agregar idioma")} />
            </div>

            <div className="grid gap-3">
              {languagesFA.fields.map((language, i) => (
                <div key={language.id} className="rounded-2xl border border-slate-200/60 bg-slate-50/80 p-3 dark:border-zinc-800/70 dark:bg-zinc-900/40">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-zinc-400">
                      {t("Resume.Idioma")} {i + 1}
                    </p>
                    <MoveButtons index={i} total={languagesFA.fields.length} onMoveUp={() => languagesFA.move(i, i - 1)} onMoveDown={() => languagesFA.move(i, i + 1)} onRemove={() => languagesFA.remove(i)} />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Controller name={`aiCVInfo.languages.${i}.name`} control={control} render={({ field }) => <Input {...field} label={t("Resume.Idioma")} placeholder={t("Resume.Idioma")} />} />
                    <Controller name={`aiCVInfo.languages.${i}.level`} control={control} render={({ field }) => <Input {...field} label={t("Resume.Nivel")} placeholder={t("Resume.Nivel")} />} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="flex justify-end pt-5">
            <Button loading={updateProfileLoading} onClick={handleSubmit(handleSaveCV)} variant="primary" size="lg" leftIcon={!updateProfileLoading ? <Save className="h-4 w-4" /> : undefined}>
              {updateProfileLoading ? "Guardando..." : t("Data.SaveChanges")}
            </Button>
          </section>
        </div>
      </Card>

      <Modal open={aiHelperOpen} onClose={() => setAiHelperOpen(false)} title={t("Resume.Title1")} description={t("Resume.Instruction1")} size="lg" panelClassName="max-h-[min(88vh,760px)]">
        <AIHelperCV />
      </Modal>
    </div>
  );
}
